import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, Input, OnDestroy, Optional, Self, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NgControl, Validators } from '@angular/forms';
import { duration, Duration, localeData } from 'moment';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
  standalone: false,
  selector: 'nimbus-filter-time-range-input',
  templateUrl: './nimbus-filter-time-range-input.component.html',
  styleUrls: ['./nimbus-filter-time-range-input.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: NimbusFilterTimeRangeInputComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterTimeRangeInputComponent implements MatFormFieldControl<Duration>, ControlValueAccessor, OnDestroy {
  public static nextId = 0;

  @ViewChild('hours', { static: true }) public hoursInput: ElementRef<HTMLInputElement>;

  @HostBinding() public id = `nimbus-time-input-${NimbusFilterTimeRangeInputComponent.nextId++}`;

  @Input() public amPmSwitcher: boolean;
  @Input() public maxHours?: number | null;
  @Input() public timeFormat?: string;
  @Input() public locale?: string;

  private internalPlaceholder: string;
  private internalRquired = false;
  private internalDisabled = false;
  private readonly meridian: number = 12;
  private readonly maxHours24h: number = 23;

  public readonly controlType: string = 'time-input';
  public readonly autofilled: boolean = false;
  public readonly stateChanges = new Subject<void>();
  public readonly userAriaDescribedBy: string;
  public readonly maxLength: number = 2;
  public focused: boolean;
  public touched: boolean;
  public form: FormGroup;

  @Input()
  public get value(): Duration | null {
    const durationFormValue = this.form.value;

    let hours = +durationFormValue.hours;

    if (this.useAmPm) {
      if (durationFormValue.meridianSwitch === 'PM' && hours < this.meridian) {
        hours = +durationFormValue.hours + this.meridian;
      } else if (durationFormValue.meridianSwitch === 'AM' && hours === this.meridian) {
        hours = 0;
      }
    }

    const d = duration({
      hours,
      minutes: durationFormValue.minutes,
      seconds: this.showSeconds ? durationFormValue.seconds : 0,
    });

    return d;
  }

  public set value(d: Duration | null) {
    if (!d) {
      return;
    }
    const totalHours = Math.floor(d.asHours());
    const hours = this.useAmPm ? totalHours % this.meridian || this.meridian : totalHours;
    this.form.setValue({
      hours: Math.floor(hours).toString().padStart(this.maxLength, '0'),
      minutes: d?.minutes().toString().padStart(this.maxLength, '0'),
      seconds: d?.seconds().toString().padStart(this.maxLength, '0'),
      meridianSwitch: totalHours >= this.meridian ? 'PM' : 'AM',
    });
    this.stateChanges.next();
  }

  @Input()
  public get placeholder(): string {
    return this.internalPlaceholder;
  }
  public set placeholder(val: string) {
    this.internalPlaceholder = val;
    this.stateChanges.next();
  }

  @Input()
  public get required(): boolean {
    return this.internalRquired;
  }
  public set required(req: boolean) {
    this.internalRquired = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @Input()
  public get disabled(): boolean {
    return this.internalDisabled;
  }
  public set disabled(value: boolean) {
    this.internalDisabled = coerceBooleanProperty(value);
    if (this.internalDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
    this.stateChanges.next();
  }

  public constructor(
    formBuilder: FormBuilder,
    @Optional() @Self() public ngControl: NgControl,
    private readonly focusMonitor: FocusMonitor,
    private readonly elementRef: ElementRef<HTMLElement>,
  ) {
    this.form = formBuilder.group({
      hours: ['00', [Validators.required]],
      minutes: ['00', [Validators.required]],
      seconds: ['00', [Validators.required]],
      meridianSwitch: ['AM'],
    });
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  public get showSeconds(): boolean {
    const resolved = this.resolvedTimeFormat;
    return resolved ? /s/.test(resolved) : true;
  }

  public get useAmPm(): boolean {
    if (this.amPmSwitcher != null) {
      return this.amPmSwitcher;
    }
    if (!this.locale) {
      return false;
    }
    const sample = this.resolvedTimeFormat || this.localeTimeFormat;
    return /A|a|h/.test(this.stripEscapes(sample));
  }

  public get effectiveMaxHours(): number | null | undefined {
    if (this.timeFormat) {
      return this.useAmPm ? this.meridian : this.maxHours24h;
    }
    return this.maxHours;
  }

  public onChange: (v: Duration) => void = (v: Duration) => {};
  public onTouched: () => void = () => {};

  public get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  public get empty(): boolean {
    const durationValue = this.form.value;
    return !durationValue.hours && !durationValue.minutes && !durationValue.seconds;
  }
  public get errorState(): boolean {
    return this.form.invalid && this.touched;
  }

  public onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elementRef.nativeElement.querySelector('input').focus();
    }
  }

  public setDescribedByIds(ids: string[]): void {
    const controlElement = this.elementRef.nativeElement.querySelector('.nimbus-time-range-input-container')!;
    if (controlElement != null) {
      controlElement.setAttribute('aria-describedby', ids.join(' '));
    }
  }

  public writeValue(val: Duration | null): void {
    this.value = val;
  }

  public registerOnChange(fn: (v: Duration) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onFocusIn(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
      this.focusMonitor.focusVia(this.hoursInput, 'program');
      this.hoursInput.nativeElement.select();
    }
  }

  public onFocusOut(event: FocusEvent): void {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  public autoFocusNext(control: AbstractControl, nextElement?: HTMLInputElement, requiredLength?: number): void {
    if (!control.errors && nextElement && control.value.length === requiredLength) {
      this.focusMonitor.focusVia(nextElement, 'program');
      nextElement.select();
    }
  }

  public autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement, noLimit: boolean = false): void {
    if (control.value.length < 1 || noLimit) {
      this.focusMonitor.focusVia(prevElement, 'program');
      prevElement.select();
    }
  }

  public handleInput(control: AbstractControl, nextElement?: HTMLInputElement, requiredLength?: number): void {
    this.autoFocusNext(control, nextElement, requiredLength);
    this.onChange(this.value);
  }

  public onBlur(control: AbstractControl): void {
    if (control.value.length < this.maxLength) {
      control.setValue(control.value.padStart(this.maxLength, '0'));
    }
  }

  public onFocus(element: HTMLInputElement): void {
    element.select();
  }

  public ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  private get resolvedTimeFormat(): string {
    if (!this.timeFormat) {
      return '';
    }
    const locale = localeData(this.locale);
    return this.timeFormat.replace(/LTS|LT|LLLL|LLL|LL|L/g, (token) => locale.longDateFormat(token as 'LT') ?? token);
  }

  private get localeTimeFormat(): string {
    return localeData(this.locale).longDateFormat('LT');
  }

  private stripEscapes(format: string): string {
    // Moment treats characters wrapped in [] as literal; strip them so a literal `a` in `[at] H:mm` isn't mistaken for am/pm.
    return format.replace(/\[[^\]]*\]/g, '');
  }
}
